import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2, Plus, GripVertical, RefreshCw, FileSpreadsheet, Download } from "lucide-react";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Panama" });

const emptyBlock = () => ({ block_id: null, title: "", description: "", result_type: "load" });

const SHEET_ERR = {
  invalid_sheet_url: { es: "Enlace no válido.", en: "Invalid link.", fr: "Lien invalide." },
  sheet_not_public: { es: "La hoja no es pública. Compártela con 'Cualquiera con el enlace'.", en: "Sheet is not public. Share it as 'Anyone with the link'.", fr: "La feuille n'est pas publique. Partage-la en 'Tous ceux ayant le lien'." },
  missing_date_column: { es: "Falta la columna 'Date'.", en: "Missing 'Date' column.", fr: "Colonne 'Date' manquante." },
  fetch_failed: { es: "No se pudo leer la hoja.", en: "Could not read the sheet.", fr: "Impossible de lire la feuille." },
};

function GoogleSheetSync() {
  const { t, tl } = useI18n();
  const [url, setUrl] = useState("");
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/workouts/sheet-config").then(({ data }) => { setCfg(data); setUrl(data.url || ""); });
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/workouts/sync-sheet", { url: url.trim() });
      toast.success(tl({ es: `${data.synced} días sincronizados`, en: `${data.synced} days synced`, fr: `${data.synced} jours synchronisés` }));
      load();
    } catch (err) {
      const code = err.response?.data?.detail;
      toast.error(tl(SHEET_ERR[code] || { es: "Error de sincronización", en: "Sync error", fr: "Erreur de synchronisation" }));
    } finally { setBusy(false); }
  };

  const lr = cfg?.last_result;

  const downloadTemplate = () => {
    const rows = [
      ["Date", "Titre", "Bloc", "Détails", "Type"],
      ["2026-07-27", "Lundi · Force + Metcon", "Échauffement", "10 min mobilité + 2 tours (10 air squats, 10 push-ups)", "free"],
      ["2026-07-27", "", "Force", "5x5 Back Squat @ 80%", "load"],
      ["2026-07-27", "", "Metcon", "21-15-9 Thrusters (43/30kg) + Pull-ups", "time"],
      ["2026-07-28", "Mardi · Gymnastique", "Skill", "EMOM 10 min : 3 Muscle-ups", "reps"],
      ["2026-07-28", "", "Cardio", "5 km Run", "time"],
      ["2026-07-29", "Mercredi · Repos actif", "Mobilité", "30 min étirements + marche", "free"],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "frenchies-seance-du-jour-modele.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="mb-6 rounded-2xl border border-border bg-card p-6" data-testid="gsheet-sync-card">
      <div className="mb-3 flex items-center gap-2">
        <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
        <h2 className="font-display text-lg font-semibold">{tl({ es: "Sincronizar desde Google Sheets", en: "Sync from Google Sheets", fr: "Synchroniser depuis Google Sheets" })}</h2>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        {tl({
          es: "Pega el enlace de tu hoja (Compartir → 'Cualquiera con el enlace'). Columnas: Date, Titre, Bloc, Détails, Type. Se sincroniza automáticamente cada 30 min.",
          en: "Paste your sheet link (Share → 'Anyone with the link'). Columns: Date, Titre, Bloc, Détails, Type. Auto-syncs every 30 min.",
          fr: "Colle le lien de ta feuille (Partager → 'Tous ceux ayant le lien'). Colonnes : Date, Titre, Bloc, Détails, Type. Synchro auto toutes les 30 min.",
        })}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input data-testid="gsheet-url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/.../edit" className="h-11" />
        <Button data-testid="gsheet-sync-btn" onClick={sync} disabled={busy || !url.trim()} className="rounded-full shrink-0">
          <RefreshCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} />{tl({ es: "Guardar y sincronizar", en: "Save & sync", fr: "Enregistrer et synchroniser" })}
        </Button>
      </div>
      <button data-testid="gsheet-template-btn" onClick={downloadTemplate}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-500 hover:underline">
        <Download className="h-3.5 w-3.5" />{tl({ es: "Descargar plantilla (CSV)", en: "Download template (CSV)", fr: "Télécharger le modèle (CSV)" })}
      </button>
      {cfg?.last_sync && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="gsheet-last-sync">
          {tl({ es: "Última sincronización", en: "Last sync", fr: "Dernière synchro" })}: {new Date(cfg.last_sync).toLocaleString("fr-FR", { timeZone: "America/Panama" })}
          {lr && !lr.error && ` · ${lr.synced} ${tl({ es: "días", en: "days", fr: "jours" })}`}
          {lr?.error && ` · ⚠️ ${tl(SHEET_ERR[lr.error] || { es: "error", en: "error", fr: "erreur" })}`}
        </p>
      )}
    </div>
  );
}

export function AdminWod() {
  const { t, tl } = useI18n();
  const [f, setF] = useState({ date: today(), title: "", description: "", blocks: [emptyBlock()], focus_tip: "", focus_video: null });
  const [list, setList] = useState([]);
  const [videos, setVideos] = useState([]);

  const load = () => api.get("/workouts").then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/videos").then(({ data }) => setVideos(data)); }, []);

  const setBlock = (i, patch) => setF((prev) => ({ ...prev, blocks: prev.blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const addBlock = () => setF((prev) => ({ ...prev, blocks: [...prev.blocks, emptyBlock()] }));
  const removeBlock = (i) => setF((prev) => ({ ...prev, blocks: prev.blocks.filter((_, idx) => idx !== i) }));

  const reset = () => setF({ date: today(), title: "", description: "", blocks: [emptyBlock()], focus_tip: "", focus_video: null });

  const save = async () => {
    if (!f.title || !f.date) return;
    const blocks = f.blocks.filter((b) => b.title.trim()).map(({ _uid, ...b }) => b);
    if (blocks.length === 0) { toast.error(tl({ es: "Añade al menos un bloque.", en: "Add at least one block.", fr: "Ajoute au moins un bloc." })); return; }
    try {
      await api.post("/admin/workouts", { date: f.date, title: f.title, description: f.description, blocks, focus_tip: f.focus_tip, focus_video: f.focus_video });
      toast.success(t("common.success"));
      reset(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const edit = (w) => setF({
    date: w.date, title: w.title, description: w.description || "",
    blocks: (w.blocks && w.blocks.length) ? w.blocks.map((b) => ({ ...b, _uid: crypto.randomUUID() })) : [emptyBlock()],
    focus_tip: w.focus_tip || "", focus_video: w.focus_video || null,
  });
  const del = async (date) => { try { await api.delete(`/admin/workouts/${date}`); toast.success(t("common.success")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const TYPES = [
    ["load", tl({ es: "Carga (lbs)", en: "Load (lbs)", fr: "Charge (lbs)" })],
    ["time", tl({ es: "Tiempo", en: "Time", fr: "Temps" })],
    ["reps", tl({ es: "Repeticiones", en: "Reps", fr: "Répétitions" })],
    ["free", tl({ es: "Libre", en: "Free", fr: "Libre" })],
  ];

  return (
    <AdminLayout>
      <h1 className="mb-6 font-display text-3xl font-bold tracking-tight">{tl({ es: "Rutina del día", en: "Workout of the day", fr: "Séance du jour" })}</h1>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">{tl({ es: "Publicar / Editar", en: "Publish / Edit", fr: "Publier / Modifier" })}</h2>
          <div className="space-y-3">
            <div><Label>{tl({ es: "Fecha", en: "Date", fr: "Date" })}</Label><Input data-testid="wod-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>{tl({ es: "Título general", en: "General title", fr: "Titre général" })}</Label><Input data-testid="wod-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={tl({ es: "Ej: Lunes · Fuerza + Metcon", en: "E.g: Monday · Strength + Metcon", fr: "Ex: Lundi · Force + Metcon" })} className="mt-1.5 h-11" /></div>
            <div><Label>{tl({ es: "Descripción general (opcional)", en: "General description (optional)", fr: "Description générale (optionnel)" })}</Label>
              <Textarea data-testid="wod-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="mt-1.5" /></div>

            <div className="rounded-xl border border-gold/40 bg-gold/5 p-3">
              <Label className="text-gold">{tl({ es: "🎯 Focus del día (consejo técnico)", en: "🎯 Daily Focus (technical tip)", fr: "🎯 Focus du jour (conseil technique)" })}</Label>
              <Textarea data-testid="wod-focus-tip" value={f.focus_tip} onChange={(e) => setF({ ...f, focus_tip: e.target.value })} rows={2} className="mt-1.5" placeholder={tl({ es: "Ej: En el squat, empuja las rodillas hacia afuera.", en: "E.g: In the squat, drive your knees out.", fr: "Ex: Au squat, pousse les genoux vers l'extérieur." })} />
              <select data-testid="wod-focus-video" value={f.focus_video?.video_id || ""} onChange={(e) => { const v = videos.find((x) => x.video_id === e.target.value); setF({ ...f, focus_video: v ? { video_id: v.video_id, title: v.title, url: v.url } : null }); }} className="mt-2 h-10 w-full rounded-lg border border-border bg-background px-2 text-sm">
                <option value="">{tl({ es: "Sin video", en: "No video", fr: "Aucune vidéo" })}</option>
                {videos.map((v) => <option key={v.video_id} value={v.video_id}>{v.title} ({v.category})</option>)}
              </select>
            </div>

            <div className="pt-2">
              <div className="mb-2 flex items-center justify-between">
                <Label>{tl({ es: "Bloques", en: "Blocks", fr: "Blocs" })} ({f.blocks.length})</Label>
              </div>
              <div className="space-y-4">
                {f.blocks.map((b, i) => (
                  <div key={b._uid} data-testid={`wod-block-${i}`} className="rounded-xl border border-border bg-background/50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <GripVertical className="h-3.5 w-3.5" /> {tl({ es: "Bloque", en: "Block", fr: "Bloc" })} {i + 1}
                      </span>
                      {f.blocks.length > 1 && (
                        <button data-testid={`remove-block-${i}`} onClick={() => removeBlock(i)} className="text-destructive hover:opacity-80" aria-label="remove block">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-2.5">
                      <Input data-testid={`block-title-${i}`} value={b.title} onChange={(e) => setBlock(i, { title: e.target.value })} placeholder={tl({ es: "Título del bloque (ej: Fuerza)", en: "Block title (e.g: Strength)", fr: "Titre du bloc (ex: Force)" })} className="h-10" />
                      <Textarea data-testid={`block-desc-${i}`} value={b.description} onChange={(e) => setBlock(i, { description: e.target.value })} rows={3} placeholder={"5x5 Back Squat @ 80%"} />
                      <div>
                        <span className="text-xs text-muted-foreground">{tl({ es: "Tipo de resultado", en: "Result type", fr: "Type de résultat" })}</span>
                        <select data-testid={`block-type-${i}`} value={b.result_type} onChange={(e) => setBlock(i, { result_type: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-border bg-background px-3">
                          {TYPES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" data-testid="add-block-btn" onClick={addBlock} className="mt-3 w-full rounded-full">
                <Plus className="mr-1.5 h-4 w-4" /> {tl({ es: "Añadir bloque", en: "Add block", fr: "Ajouter un bloc" })}
              </Button>
            </div>

            <div className="flex gap-2 pt-2">
              <Button data-testid="wod-save" onClick={save} disabled={!f.title} className="rounded-full">{t("common.save")}</Button>
              <Button variant="ghost" data-testid="wod-reset" onClick={reset} className="rounded-full">{tl({ es: "Nuevo", en: "New", fr: "Nouveau" })}</Button>
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-4 font-display text-lg font-semibold">{tl({ es: "Historial", en: "History", fr: "Historique" })}</h2>
          <div className="space-y-2">
            {list.length === 0 && <p className="text-sm text-muted-foreground">{t("common.noData")}</p>}
            {list.map((w) => (
              <div key={w.workout_id} data-testid={`admin-wod-${w.date}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">{w.date} · {(w.blocks || []).length} {tl({ es: "bloques", en: "blocks", fr: "blocs" })}</div>
                  <div className="truncate font-medium">{w.title}</div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button size="sm" variant="outline" data-testid={`edit-wod-${w.date}`} onClick={() => edit(w)} className="rounded-full">{tl({ es: "Editar", en: "Edit", fr: "Modifier" })}</Button>
                  <Button size="sm" variant="ghost" data-testid={`del-wod-${w.date}`} onClick={() => del(w.date)} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <p className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">{tl({ es: "Opción avanzada", en: "Advanced option", fr: "Option avancée" })}</p>
        <GoogleSheetSync />
      </div>
    </AdminLayout>
  );
}
